/**
 * PostHog `before_send` filter that drops un-actionable browser network-noise
 * `$exception` events before they reach Error Tracking.
 *
 * Why this exists (BRE / app-wide "Load failed" alert):
 * posthog-js exception autocapture (enabled implicitly by `defaults`) hooks
 * `window.onunhandledrejection` / `window.onerror`. When an in-flight fetch
 * rejects at the *network layer* — a long-idle Safari tab resumes and aborts
 * its requests, the user navigates away mid-request, a connection drops — the
 * browser produces a bare `TypeError`:
 *   - Safari : "Load failed"
 *   - Chrome : "Failed to fetch"
 *   - Firefox: "NetworkError when attempting to fetch resource"
 * Safari additionally strips the stack on these, so PostHog records a
 * contextless `$exception` with "(no stack captured)" and no request URL.
 *
 * These are NOT logic defects: by the `fetch()` error model a backend 5xx
 * *resolves* the promise with `response.ok === false`, so it can never be the
 * source of "Load failed". CloudWatch confirmed no server error correlates
 * with the reported occurrences, and the data layer self-heals (RxDB pull
 * handlers catch + retry; TanStack queries refetch on reconnect).
 *
 * Scope note: the PRIMARY in-app source — the periodic tombstone-reconcile
 * server-action fetch leaking via a `void`-discarded interval — is fixed at
 * the root in `rxdb/utils/lifecycle-hooks.ts` (handled, not suppressed). This
 * filter exists for the RESIDUAL we cannot fix in our code: Next.js App Router
 * navigation / RSC-prefetch transport fetches that Safari aborts on tab
 * resume (the same event surfaces as the companion Next.js E394 "unexpected
 * response", see `route-error-classification.ts`). Those are framework-internal
 * and un-actionable, so we drop them rather than carry them as Error Tracking
 * noise.
 *
 * This filter is deliberately CONSERVATIVE so it never hides a real bug:
 *  - Only `$exception` events are eligible.
 *  - EVERY exception in the event's `$exception_list` must independently match
 *    (a single non-network exception in the group keeps the whole event).
 *  - A network `TypeError` is only dropped when it has NO stack frames. A real
 *    fetch bug originating in our code carries a stack pointing into our
 *    modules and stays reportable; only the stackless browser-origin noise is
 *    suppressed.
 *  - `AbortError` is dropped regardless of stack — it is always an intentional
 *    cancellation (AbortController / navigation), never a defect.
 */

import type { CaptureResult } from "posthog-js";
import { NETWORK_FAILURE_MESSAGE_SUBSTRINGS } from "@/lib/network-error";

interface ExceptionListItem {
  type?: unknown;
  value?: unknown;
  stacktrace?: { frames?: unknown } | unknown;
}

function hasStackFrames(item: ExceptionListItem): boolean {
  const stacktrace = item.stacktrace;
  if (typeof stacktrace !== "object" || stacktrace === null) return false;
  const frames = (stacktrace as { frames?: unknown }).frames;
  return Array.isArray(frames) && frames.length > 0;
}

/** A single autocaptured exception is benign browser network noise. */
function isBenignNetworkException(item: ExceptionListItem): boolean {
  const type = typeof item.type === "string" ? item.type : "";
  const value = typeof item.value === "string" ? item.value.toLowerCase() : "";

  // Intentional cancellation — always benign, stack or not.
  if (type === "AbortError") return true;

  // Network-layer fetch rejection. Require an empty stack: a real fetch bug in
  // our code carries frames and should remain reportable.
  const isNetworkTypeError =
    type === "TypeError" &&
    NETWORK_FAILURE_MESSAGE_SUBSTRINGS.some((m) => value.includes(m));

  return isNetworkTypeError && !hasStackFrames(item);
}

/**
 * Returns `true` when the captured event is an `$exception` whose entire
 * exception group is benign browser network noise and should be dropped.
 */
export function isBrowserNetworkNoiseException(
  cr: CaptureResult | null,
): boolean {
  if (!cr || cr.event !== "$exception") return false;

  const list = (cr.properties as Record<string, unknown> | undefined)?.[
    "$exception_list"
  ];
  if (!Array.isArray(list) || list.length === 0) return false;

  return list.every((item) =>
    isBenignNetworkException((item ?? {}) as ExceptionListItem),
  );
}

const BROKEN_STORAGE_MESSAGE = /opening backing store|docs\.bulkPut\(\).*AbortError/;

/** A rejection from RxDB's checkpoint queue, which has no handler we can reach. */
export function isBrokenStorageException(cr: CaptureResult | null): boolean {
  if (!cr || cr.event !== "$exception") return false;
  const list = (cr.properties as Record<string, unknown> | undefined)?.[
    "$exception_list"
  ];
  return (
    Array.isArray(list) &&
    list.some((item) => {
      const value = (item as ExceptionListItem | null)?.value;
      return typeof value === "string" && BROKEN_STORAGE_MESSAGE.test(value);
    })
  );
}

// BRE-9757: once replication's queue rejects, every trigger rejects again until
// reload. The first one per page load is the signal; the rest would flood alerts.
let brokenStorageExceptionSeen = false;

/**
 * posthog-js `before_send` hook. Drops browser network-noise `$exception`
 * events and repeats of a broken-storage one (returns `null`); passes
 * everything else through untouched.
 *
 * Fails open: this runs on EVERY captured event, so a bug in the classifier
 * must never break telemetry. On any unexpected error we keep the event.
 */
export function filterBrowserNetworkNoise(
  cr: CaptureResult | null,
): CaptureResult | null {
  try {
    if (isBrowserNetworkNoiseException(cr)) return null;
    if (isBrokenStorageException(cr)) {
      if (brokenStorageExceptionSeen) return null;
      brokenStorageExceptionSeen = true;
    }
  } catch {
    // Keep the event — never drop telemetry because the filter threw.
  }
  return cr;
}
